/** @jsx jsx */

/** @jsxFrag */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext, useEffect, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';

import * as Sentry from '@sentry/react';
import { LearnerProfile_EducationHistoryDegreeType as DegreeTypeConstant } from '__generated__/graphql-types';
import type {
  LearnerProfile_EducationHistoryDegreeType as DegreeType,
  LearnerProfile_EducationHistoryMajor as MajorType,
} from '__generated__/graphql-types';
import type {
  LearnerProfile_EducationHistory as EducationHistory,
  LearnerProfile_YearMonthDate as ProfileDate,
} from '__generated__/graphql-types-do-not-use';

import {
  Button,
  Dialog,
  Divider,
  InlineNotification,
  SelectField,
  SelectOption,
  StandaloneCheckbox,
  TextField,
  Typography2,
} from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';
import { TrashIcon } from '@coursera/cds-icons';

import { getEducationChoicesLearnerProfileV1, getMajorChoices } from 'bundles/account-profile/common/constants';
import type { LearnerProfile as ProfileType } from 'bundles/account-profile/common/types';
import UniversitySelectDropdown from 'bundles/account-profile/components/common/UniversitySelectDropdown';
import { OccupationRolesContext } from 'bundles/account-profile/components/recruiting-profile-viewer/OccupationRolesProvider';
import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import RequiredFieldLabel from 'bundles/account-profile/components/recruiting-profile-viewer/RequiredFieldLabel';
import ModalDateSection from 'bundles/account-profile/components/recruiting-profile-viewer/components/ModalDateSection';
import type { ValidationErrors } from 'bundles/account-profile/components/recruiting-profile-viewer/utils/validationUtils';
import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import TrackedButton from 'bundles/page/components/TrackedButton';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/account-profile';

type EditDialogProps = {
  isOpen: boolean;
  handleClose: () => void;
  education?: EducationHistory;
};

const getFormErrorKeys = () => ({
  institutionName: _t('name of institution'),
  degreeType: _t('degree'),
  major: _t('discipline'),
  startDate: _t('start date'),
  graduationDate: _t('graduation date'),
  grade: _t('grade'),
});

type RequiredFields = keyof ReturnType<typeof getFormErrorKeys>;
type FormFieldRefs = Record<RequiredFields, MutableRefObject<HTMLDivElement | null>>;

type FormErrors = Record<string, string | undefined>;

const styles = {
  formWrapper: () => css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-300);

    input[type='text'],
    input[type='number'] {
      margin-bottom: 0;
    }
  `,
  removeButton: () => css`
    margin-left: var(--cds-spacing-300);
  `,
  withMarginTop: (theme: Theme) => css`
    margin-top: var(--cds-spacing-100);
  `,
  formInput: css`
    label {
      margin-bottom: 0;
    }
  `,
  actionButtons: css`
    > div {
      display: inline; /* TrackedButton adds a wrapping div which is unstyled and puts the action buttons on separate lines */
    }
  `,
};

const E2E_SELECTORS = {
  DEGREE: 'profile-education-degree',
  DISCIPLINE: 'profile-education-discipline',
  START: 'profile-education-start-date',
  END: 'profile-education-end-date',
  STUDYING_CHECKBOX: 'profile-education-study',
  SAVE: 'profile-education-save',
} as const;

const NO_DICIPLINE_DEGREE_TYPES: DegreeType[] = [DegreeTypeConstant.NoDegree, DegreeTypeConstant.HighSchoolDiploma];
const MAX_AREA_OF_CONCENTRATION_LENGTH = 50;

const educationConvert = (values: EducationHistory) => {
  const edHistory = {
    ...values,
    grade: values.grade && values.degreeType !== DegreeTypeConstant.NoDegree ? values.grade : undefined,
  };

  if (NO_DICIPLINE_DEGREE_TYPES.includes(edHistory.degreeType as DegreeType)) {
    edHistory.major = undefined;
    edHistory.areaOfConcentration = undefined;
  }

  return edHistory;
};

const CredentialsSectionEducationEditDialog = ({ isOpen, handleClose, education }: EditDialogProps) => {
  const { HeadingGroup, Content, Actions } = Dialog;
  const { getSelectedTalentPipelineRoleIds } = useContext(OccupationRolesContext);
  const { updateLearnerProfile, profileState = {}, restrictHiringSolutions } = useContext(ProfileContext);
  const { educationHistory = [] } = profileState;
  const [eduFormValues, setEduFormValues] = useState<EducationHistory>(education || {});
  const [eduFormErrors, setEduFormErrors] = useState<FormErrors>({});
  const [submittingForm, setSubmittingForm] = useState<boolean>(false);

  const formFieldRefs: FormFieldRefs = {
    startDate: useRef(null),
    graduationDate: useRef(null),
    institutionName: useRef(null),
    degreeType: useRef(null),
    major: useRef(null),
    grade: useRef(null),
  };

  const scrollToFirstError = (validationErrors: ValidationErrors) => {
    // I specifically use a map so that I can iterate over it in order. This way, we are guaranteed that scrolling
    // to the first error in scrollToFirstError will bring us to the actual first error
    const errorKeys = new Map(Object.entries(getFormErrorKeys()));
    const orderedKeys: string[] = [];
    errorKeys.forEach((value, key) => {
      if (validationErrors[key] !== undefined) {
        orderedKeys.push(key);
      }
    });

    const firstError = orderedKeys[0];
    const firstErrorRef = formFieldRefs[firstError as RequiredFields];
    firstErrorRef?.current?.scrollIntoView?.({ behavior: 'smooth', block: 'end' });
  };

  const selectedTalentPipelineRoleIds = getSelectedTalentPipelineRoleIds(profileState?.occupationPreferences);
  const hasTalentPipelineRole = selectedTalentPipelineRoleIds.length > 0;
  const isEdit = !!education;
  const educationChoices = getEducationChoicesLearnerProfileV1();
  const majorChoicesArray = getMajorChoices();

  const getIsSaveButtonDisabled = () => {
    // The "I currently study here" adds no real UI data, and if we select and unselect it we will still have eduFormValues
    return Object.keys(eduFormValues).filter((key) => key !== 'isCurrentlyAStudent').length === 0;
  };

  const validateGrade = (grade: string) => {
    if (grade === null || grade === undefined) {
      return { grade: _t('Please input a grade.') };
    }

    if (grade.length > 100) {
      return { grade: _t('Grade should be no more than 100 characters.') };
    }

    return { grade: undefined };
  };

  const validateDates = () => {
    const errors: FormErrors = {
      startDate: undefined,
      graduationDate: undefined,
    };
    const { startDate, graduationDate, isCurrentlyAStudent } = eduFormValues;

    // startDate validation
    if (startDate && !startDate.month) {
      errors.startDate = _t('Please select a month');
    } else if (startDate && !startDate.year) {
      errors.startDate = _t('Please select a year');
    }

    // graduationDate validation
    if (graduationDate && !graduationDate.month) {
      errors.graduationDate = _t('Please select a month');
    } else if (graduationDate && !graduationDate.year) {
      errors.graduationDate = _t('Please select a year');
    } else if (graduationDate) {
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth() + 1;
      if (
        isCurrentlyAStudent &&
        (graduationDate.year < currentYear ||
          (graduationDate.year === currentYear && graduationDate.month < currentMonth))
      ) {
        // currently a student, graduation date must be a future date
        errors.graduationDate = _t('Please select a future date');
      } else if (
        !isCurrentlyAStudent &&
        (graduationDate.year > currentYear ||
          (graduationDate.year === currentYear && graduationDate.month > currentMonth))
      ) {
        // currently not a student, graduation date must before today
        errors.graduationDate = _t('Please select a date before today');
      }
    }

    // startDate and graduationDate validation
    if (!errors.startDate && !errors.graduationDate) {
      const startDateTime = new Date(startDate?.year as number, startDate?.month as number);
      const graduationDateTime = new Date(graduationDate?.year as number, graduationDate?.month as number);
      if (graduationDateTime <= startDateTime) {
        errors.startDate = _t('Graduation date must be later than start date.');
        errors.graduationDate = _t('Graduation date must be later than start date.');
      }
    }

    return errors;
  };

  const onSubmit = async () => {
    const errors: FormErrors = {
      ...eduFormErrors,
      ...validateDates(),
    };

    const filteredErrors = Object.entries(errors).reduce((acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = errors[key];
      }

      return acc;
    }, {} as FormErrors);

    const hasErrors = Object.keys(filteredErrors).length > 0;

    if (hasErrors) {
      setEduFormErrors(errors);
      scrollToFirstError(filteredErrors);
    } else {
      setEduFormErrors({});
      try {
        const educationHistoryCopy = [...educationHistory];
        const converted = educationConvert(eduFormValues);
        if (isEdit) {
          const editIndex = educationHistoryCopy.findIndex((eduHistory) => eduHistory === education);
          if (editIndex !== -1) {
            educationHistoryCopy[editIndex] = converted;
          }
        } else {
          educationHistoryCopy.push(converted);
        }

        setSubmittingForm(true);
        await updateLearnerProfile({
          educationHistory: educationHistoryCopy,
        });
        setSubmittingForm(false);
        setEduFormValues({});
        handleClose();
      } catch (e) {
        setSubmittingForm(false);
        Sentry.captureException(e);
      }
    }
  };

  const onDelete = async () => {
    try {
      const educationHistoryCopy = [...educationHistory];
      const editIndex = educationHistoryCopy.findIndex((eduHistory) => eduHistory === education);
      if (editIndex !== -1) {
        educationHistoryCopy.splice(editIndex, 1);
      }

      const updatedFields: Partial<ProfileType> = {
        educationHistory: educationHistoryCopy,
      };
      await updateLearnerProfile(updatedFields);

      setSubmittingForm(false);
      setEduFormValues({});
      handleClose();
    } catch (error) {
      setSubmittingForm(false);

      throw new Error(error);
    }
  };

  const onClose = () => {
    // reset states
    setSubmittingForm(false);
    setEduFormErrors({});
    handleClose();
  };

  useEffect(() => {
    setEduFormValues(education || {});
  }, [education]);

  const getSupportText = () => {
    if (restrictHiringSolutions) {
      return _t(
        'Add your educational background to let viewers know where you studied or are currently studying. Even if you didn’t finish, it’s important to include it here. And if you’ve earned a college degree, you don’t need to add your high school/GED.'
      );
    }

    if (hasTalentPipelineRole) {
      return _t(
        'Add your educational background to let employers know where you studied or are currently studying. Even if you didn’t finish, it’s important to include it here. And if you’ve earned a college degree, you don’t need to add your high school/GED.'
      );
    }
    return _t(
      'Add your educational background to let employers know where you studied or are currently studying. Even if you didn’t finish, it’s important to include it here. And if you’ve earned a college degree, you don’t need to add your high school/GED. All fields are optional.'
    );
  };

  return (
    <Dialog aria-describedby="dialog-content" open={isOpen} variant="standard" onClose={onClose}>
      <HeadingGroup supportText={getSupportText()}>{_t('Education')}</HeadingGroup>
      <Content id="dialog-content">
        <TrackedDiv
          trackClicks={false}
          withVisibilityTracking
          // not tracked in eventing v3
          trackingName="profile_education_history_dialog"
          requireFullyVisible={false}
          css={styles.formWrapper}
        >
          <div>
            <Typography2 variant="subtitleMedium" component="label">
              <RequiredFieldLabel labelComponent={<span>{_t('Name of institution')}</span>} />
            </Typography2>
            <div css={styles.withMarginTop} ref={formFieldRefs.institutionName}>
              <UniversitySelectDropdown
                value={eduFormValues.institutionName ?? ''}
                onSelect={(name: string) => setEduFormValues((prev) => ({ ...prev, institutionName: name }))}
                errorMessage={eduFormErrors.institutionName}
              />
            </div>
          </div>
          <SelectField
            fullWidth
            label={_t('Degree')}
            renderLabel={(labelComponent: React.ReactElement) => <RequiredFieldLabel labelComponent={labelComponent} />}
            value={eduFormValues.degreeType ?? ''}
            onChange={(e) => setEduFormValues((prev) => ({ ...prev, degreeType: e.target.value as DegreeType }))}
            placeholder={_t('Select Degree')}
            data-e2e={E2E_SELECTORS.DEGREE}
            css={styles.formInput}
            validationStatus={eduFormErrors.degreeType ? 'error' : undefined}
            validationLabel={eduFormErrors.degreeType}
            ref={formFieldRefs.degreeType}
          >
            {educationChoices.map((choice) => (
              <SelectOption key={choice.name} value={choice.name}>
                {choice.value}
              </SelectOption>
            ))}
          </SelectField>
          {eduFormValues.degreeType && !NO_DICIPLINE_DEGREE_TYPES.includes(eduFormValues.degreeType) && (
            <>
              <SelectField
                fullWidth
                label={_t('Discipline')}
                renderLabel={(labelComponent: React.ReactElement) => (
                  <RequiredFieldLabel labelComponent={labelComponent} />
                )}
                value={eduFormValues.major ?? ''}
                onChange={(e) => setEduFormValues((prev) => ({ ...prev, major: e.target.value as MajorType }))}
                placeholder={_t('Select a Discipline')}
                css={styles.formInput}
                data-e2e={E2E_SELECTORS.DISCIPLINE}
                validationStatus={eduFormErrors.major ? 'error' : undefined}
                validationLabel={eduFormErrors.major}
                ref={formFieldRefs.major}
              >
                {majorChoicesArray.map((major) => (
                  <SelectOption key={major.name} value={major.name}>
                    {major.value}
                  </SelectOption>
                ))}
              </SelectField>
              <TextField
                fullWidth
                label={_t('Area of concentration')}
                type="text"
                value={eduFormValues.areaOfConcentration ?? ''}
                onChange={(e) => setEduFormValues((prev) => ({ ...prev, areaOfConcentration: e.target.value }))}
                placeholder={_t('Enter a topic or subject area')}
                css={styles.formInput}
                inputProps={{ required: false }}
                // CDS enforces the character limit if we include maxLength
                maxLength={MAX_AREA_OF_CONCENTRATION_LENGTH}
                characterLimit={MAX_AREA_OF_CONCENTRATION_LENGTH}
              />
            </>
          )}
          <Divider color="dark" />

          <ModalDateSection
            data-e2e={E2E_SELECTORS.START}
            ref={formFieldRefs.startDate}
            title={_t('Start date')}
            selectedMonth={eduFormValues.startDate?.month}
            selectedYear={eduFormValues.startDate?.year}
            handleMonthChange={(newMonth) =>
              setEduFormValues((prev) => ({
                ...prev,
                startDate: { ...(prev.startDate ?? ({} as ProfileDate)), month: newMonth },
              }))
            }
            handleYearChange={(newYear) =>
              setEduFormValues((prev) => ({
                ...prev,
                startDate: { ...(prev.startDate ?? ({} as ProfileDate)), year: newYear },
              }))
            }
            validationStatus={eduFormErrors.startDate}
          />
          <Divider color="dark" />

          <ModalDateSection
            ref={formFieldRefs.graduationDate}
            data-e2e={E2E_SELECTORS.END}
            isRequired={true}
            title={_t('Graduation date or expected graduation date')}
            selectedMonth={eduFormValues.graduationDate?.month}
            selectedYear={eduFormValues.graduationDate?.year}
            yearOffset={-8}
            handleMonthChange={(newMonth) =>
              setEduFormValues((prev) => ({
                ...prev,
                graduationDate: { ...(prev.graduationDate ?? ({} as ProfileDate)), month: newMonth },
              }))
            }
            handleYearChange={(newYear) =>
              setEduFormValues((prev) => ({
                ...prev,
                graduationDate: { ...(prev.graduationDate ?? ({} as ProfileDate)), year: newYear },
              }))
            }
            validationStatus={eduFormErrors.graduationDate}
          />
          <StandaloneCheckbox
            value={_t('isCurrentlyAStudent')}
            label={_t('I currently study here')}
            data-e2e={E2E_SELECTORS.STUDYING_CHECKBOX}
            checked={eduFormValues.isCurrentlyAStudent ?? false}
            onChange={(_, isChecked) => setEduFormValues((prev) => ({ ...prev, isCurrentlyAStudent: isChecked }))}
          />
          {eduFormValues.degreeType && eduFormValues.degreeType !== DegreeTypeConstant.NoDegree && (
            <>
              <Divider color="dark" />
              <TextField
                fullWidth
                label={_t('Cumulative grade')}
                placeholder={_t('Enter your cumulative grade for your school (e.g. GPA, CGPA, etc.)')}
                inputProps={{
                  required: false,
                }}
                value={eduFormValues.grade ?? ''}
                onChange={(e) => {
                  const inputValue = e.target.value;
                  setEduFormErrors((prev) => ({
                    ...prev,
                    grade: validateGrade(inputValue).grade,
                  }));

                  setEduFormValues((prev) => ({
                    ...prev,
                    grade: inputValue,
                  }));
                }}
                validationStatus={eduFormErrors.grade ? 'error' : undefined}
                validationLabel={eduFormErrors.grade}
                css={styles.formInput}
                maxLength={100}
                ref={formFieldRefs.grade as React.MutableRefObject<HTMLInputElement | null>}
              />
            </>
          )}
        </TrackedDiv>
      </Content>
      <Actions>
        <div css={styles.actionButtons}>
          <>
            <Button
              variant="primary"
              onClick={onSubmit}
              iconPosition="before"
              disabled={submittingForm || getIsSaveButtonDisabled()}
              loading={submittingForm}
              component={TrackedButton}
              data-e2e={E2E_SELECTORS.SAVE}
              // not tracked in eventing v3
              trackingName="profile_save_education_history"
              requireFullyVisible={false}
              withVisibilityTracking={true}
            >
              {_t('Save')}
            </Button>

            {isEdit && (
              <Button
                variant="secondary"
                onClick={onDelete}
                iconPosition="before"
                icon={<TrashIcon />}
                css={styles.removeButton}
                disabled={submittingForm}
                component={TrackedButton}
                // not tracked in eventing v3
                trackingName="profile_remove_education_history"
                requireFullyVisible={false}
                withVisibilityTracking={true}
              >
                {_t('Remove')}
              </Button>
            )}
          </>
        </div>
      </Actions>
    </Dialog>
  );
};

const ErrorBoundEducationEditDialog = (props: EditDialogProps) => (
  <ErrorBoundaryWithTagging
    componentName="CredentialsSectionEducationEditDialog"
    errorComponent={
      <InlineNotification severity="error" title={_t('There has been an error')} role="alert">
        {_t('There has been an error in the education edit dialog. Our development team will be alerted.')}
      </InlineNotification>
    }
  >
    <CredentialsSectionEducationEditDialog {...props} />
  </ErrorBoundaryWithTagging>
);

export default ErrorBoundEducationEditDialog;
